import React, { useMemo } from 'react';
import { ArrowRight } from '@phosphor-icons/react';
import CarCard from '../components/CarCard';
import SalonCarCard from '../components/landing/SalonCarCard';
import SalonHeader from '../components/landing/SalonHeader';
import AutoScrollCarousel from '../components/ui/AutoScrollCarousel';
import { Button } from '../components/ui/button';
import { useCars } from '../context/CarsContext';
import { useDealerPreset } from '../config/dealerPresets';
import { useTranslation } from '../i18n/I18nContext';
import { useDealerScopeNavigate } from '../hooks/useDealerScopeNavigate';
import { useHideOnScrollDown } from '../hooks/useHideOnScrollDown';
import { config } from '../config/runtimeConfig';
import { getDisplayImages } from '../utils/carImage';
import './ShowroomPage.css';

/**
 * Showroom catalog (issue #965). Minimal version of the car list:
 * logo-only header, card grid, no filters / footer / search. Spawned
 * by the close (×) button on a mini-landing. Tap a card → mini-landing
 * for that car (`/cars/:id/landing`) — same target as the QR flow.
 */
const ShowroomPage = () => {
  // Переносит dealer-scope параметры (?token) вперёд, чтобы reload лендинга
  // не потерял дилера и не упал в «Car not found».
  const dealerNavigate = useDealerScopeNavigate();
  const { getAllCars, loading, error } = useCars();
  const { t } = useTranslation();
  const cars = getAllCars();
  // Apply the Autodis preset (black surface, 8px card / 4px CTA radii,
  // white-on-black widget) for the lifetime of this route. The hook
  // snapshots + restores so other routes are unaffected.
  //
  // Витрина ShiftGears (#1441) — без пресета: светлая тема самой витрины
  // (серое полотно, белые скруглённые карточки), класс `showroom-light`
  // вместо `showroom`, чтобы тёмные правила Autodis из ShowroomPage.css к
  // ней не применялись вовсе.
  const isAutodis = config.SHOWROOM_BRAND === 'autodis';
  useDealerPreset(isAutodis ? 'autodis' : null);
  // Шапка витрины прилипает и уходит на прокрутке вниз, возвращаясь на
  // прокрутке вверх (#1456). Тёмный каталог Autodis — как был.
  const [headerRef, headerHidden] = useHideOnScrollDown(!isAutodis);

  // Фон витрины — размытое фото первой машины с фотографией, тем же приёмом,
  // что на свайп-лендинге и Welcome Autodis (#1456). URL уходит переменной:
  // сам фон и размытие описаны в CSS.
  const photo = useMemo(() => {
    if (isAutodis) return null;
    const withImg = cars.find((c) => getDisplayImages(c).length > 0);
    return withImg ? getDisplayImages(withImg)[0] : null;
  }, [cars, isAutodis]);
  const shellStyle = photo ? { '--showroom-photo': `url("${photo}")` } : undefined;

  const handleCardClick = (car) => {
    dealerNavigate(`/cars/${car.id}/landing`);
  };

  return (
    <div className={isAutodis ? 'showroom' : 'showroom-light'} style={shellStyle}>
      {/* Общий header — Autodis-лого по центру (кликабельно, → /salon
          Welcome), language selector справа. Brands-ряд скрыт здесь
          (на каталог-grid дополнительные lookalike-плашки избыточны).
          (#1022 — Маша 23.06 «общий хедер»). */}
      <div
        ref={headerRef}
        className={`showroom__header${headerHidden ? ' showroom__header--hidden' : ''}`}
      >
        <SalonHeader
          brands={[]}
          showLanguage
          {...(isAutodis
            ? {}
            : { logo: { src: config.DEALER_LOGO_URL, alt: 'ShiftGears' }, homePath: '/' })}
        />
      </div>

      <main className="showroom__main">
        {loading && (
          <div className="showroom__state">{t('salon.state.loading')}</div>
        )}
        {!loading && error && (
          <div className="showroom__state showroom__empty">
            {t('salon.state.error')}
          </div>
        )}
        {!loading && !error && cars.length === 0 && (
          <div className="showroom__state showroom__empty">
            {t('salon.state.empty')}
          </div>
        )}
        {!loading && !error && cars.length > 0 && isAutodis && (
          <div className="showroom__grid">
            {cars.map((car) => (
              <CarCard
                key={car.id}
                car={car}
                onCardClick={handleCardClick}
              />
            ))}
          </div>
        )}
        {/* Витрина (#1456) — не сетка, а один экран с горизонтальной
            каруселью, как Welcome салона Autodis: каталог по QR открывают с
            телефона у машины, и листать ленту пальцем там привычнее, чем
            мотать сетку из сотни карточек. Карточка — та же slim-карточка
            салона (`SalonCarCard`): у неё нет своей галереи фото, и свайп по
            ней не спорит со свайпом ленты. Весь сток — по кнопке под лентой. */}
        {!loading && !error && cars.length > 0 && !isAutodis && (
          <>
            <AutoScrollCarousel ariaLabel={t('salon.aria.carousel')}>
              {cars.map((car) => (
                <SalonCarCard
                  key={car.id}
                  car={car}
                  onClick={handleCardClick}
                  showStatus
                />
              ))}
            </AutoScrollCarousel>
            <Button
              variant="text"
              className="showroom-light__cta"
              onClick={() => dealerNavigate('/cars')}
            >
              {t('salon.cta.viewAllCars')}
              <ArrowRight size={20} aria-hidden="true" />
            </Button>
          </>
        )}
      </main>
    </div>
  );
};

export default ShowroomPage;
